// Contact card on the share page, shared by the PayPlan and National Debtline cards (PB-330, PB-334).
// Opens as a form until details are saved, then shows the saved view with the call-time choice.
import React, { useEffect, useRef, useState } from "react";
import { HiOutlineSpeakerphone } from "react-icons/hi";
import {
  Box,
  Button,
  Checkbox,
  CheckCircleIcon,
  EditIcon,
  EmailIcon,
  IconButton,
  InfoOutlinedIcon,
  PhoneIcon,
  Typography,
} from "@/ui";
import { cleanPhoneNumber, formatPhoneNumber, validatePhoneNumber } from "@/shared/lib/utils/phoneNumber";
import type { CallTime, ContactDetails } from "@/shared/share/preferencePayload";
import { ThemeColours } from "@/clients/acme/config/colours";


// Call time slots offered when the customer picks "Choose a time".
const TIME_OPTIONS: Exclude<CallTime, "Anytime">[] = ["Morning", "Afternoon", "Evening"];

// Slot labels per company: National Debtline keeps its time ranges, PayPlan uses "around" times.
const NDL_TIME_LABELS: Record<string, string> = {
  Morning: "Morning (9–12)",
  Afternoon: "Afternoon (12–3)",
  Evening: "Evening (3–7)",
};
const PAYPLAN_TIME_LABELS: Record<string, string> = {
  Morning: "Morning (around 10am)",
  Afternoon: "Afternoon (around 2pm)",
  Evening: "Evening (around 7pm)",
};
const timeLabels = (companyName: string): Record<string, string> =>
  companyName === "National Debtline" ? NDL_TIME_LABELS : PAYPLAN_TIME_LABELS;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GREEN = ThemeColours.buttonVar1;
const DARK_GREEN = ThemeColours.titleVar2;
const PALE_GREEN = ThemeColours.paleGreen;
const RED = ThemeColours.error;

interface ContactPreferenceCardProps {
  companyName: string;
  // Info icon next to the call-time heading, shown only when provided.
  onInfoClick?: (() => void) | undefined;
  // Saved details; undefined means nothing saved yet, so the form opens.
  details?: ContactDetails | undefined;
  // Pre-filled values from the profile, used when the form opens.
  defaultMobile: string;
  defaultEmail: string;
  showWhatsApp: boolean;
  // PayPlan offers "Anytime" or "Choose a time"; National Debtline always needs one of the three slots.
  allowAnytime: boolean;
  // Resolve to false when the save fails, so the form stays open.
  onSave: (details: ContactDetails) => void | Promise<boolean>;
  onCallTimeChange: (callTime: CallTime | null) => void;
  onErrorChange?: (hasError: boolean) => void;
  onEditingChange?: (editing: boolean) => void;
}

// Lets the clickable radio rows be chosen with Enter or Space as well.
const onKeyActivate = (action: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    action();
  }
};

// Hand-drawn radio so no direct MUI import is needed.
function RadioDot({ selected, light }: { selected: boolean; light?: boolean }): JSX.Element {
  const colour = light ? "#fff" : DARK_GREEN;
  return (
    <Box
      sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        border: `2px solid ${colour}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {selected && <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colour }} />}
    </Box>
  );
}

// Call-time slot rows (same design for PayPlan and National Debtline, labels differ per company).
function SlotRows({
  companyName,
  selected,
  onSelect,
}: {
  companyName: string;
  selected?: string | undefined;
  onSelect: (slot: Exclude<CallTime, "Anytime">) => void;
}): JSX.Element {
  return (
    <>
      {TIME_OPTIONS.map((slot) => (
        <Box
          key={slot}
          role="radio"
          aria-checked={selected === slot}
          tabIndex={0}
          onClick={() => onSelect(slot)}
          onKeyDown={onKeyActivate(() => onSelect(slot))}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            mt: 1,
            borderRadius: 2,
            cursor: "pointer",
            fontWeight: 600,
            color: DARK_GREEN,
            border: "1px solid #d5d5d5",
          }}
        >
          {timeLabels(companyName)[slot]}
          <RadioDot selected={selected === slot} />
        </Box>
      ))}
    </>
  );
}

// Small grey note shown on a company card, e.g. below the "Add To Share" box.
export function FeeLine({ text }: { text: string }): JSX.Element {
  const bold = "You won’t be charged.";
  const at = text.indexOf(bold);
  return (
    <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: ThemeColours.bodyVar1, mt: 2 }}>
      {at < 0 ? text : text.slice(0, at)}
      {at >= 0 && <strong>{bold}</strong>}
    </Typography>
  );
}

function ContactPreferenceCard({
  companyName,
  details,
  defaultMobile,
  defaultEmail,
  showWhatsApp,
  allowAnytime,
  onSave,
  onCallTimeChange,
  onErrorChange,
  onEditingChange,
  onInfoClick,
}: ContactPreferenceCardProps): JSX.Element {
  // The parent's saved details win; our own copy covers the moment right after Save.
  const [lastSaved, setLastSaved] = useState<ContactDetails | undefined>(undefined);
  const saved = details ?? lastSaved;
  // WhatsApp starts ticked where offered; a saved phone choice keeps its value.
  const whatsAppFor = (d?: ContactDetails): boolean => (d?.method === "phone" ? d.whatsApp : showWhatsApp);
  // Form open until something is saved; the pencil in the saved view reopens it.
  const [editing, setEditing] = useState(!details);
  const [method, setMethod] = useState<"phone" | "email">(details?.method ?? "phone");
  const [mobile, setMobile] = useState(details?.mobile ?? defaultMobile);
  const [whatsApp, setWhatsApp] = useState(whatsAppFor(details));
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  // "Choose a time" panel is open when a slot is saved or the customer opens it.
  const [showSlots, setShowSlots] = useState(
    details !== undefined && details.callTime !== "Anytime",
  );

  // Tell the parent while the form is open (unsaved edit).
  useEffect(() => onEditingChange?.(editing), [editing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Details can arrive after the card first renders (profile loads late): show them once they do.
  const hasDetails = details !== undefined;
  useEffect(() => {
    if (!details) return;
    setMethod(details.method);
    setMobile(details.mobile);
    setWhatsApp(whatsAppFor(details));
    setShowSlots(details.callTime !== "Anytime");
    setEditing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDetails]);

  // The profile number or email changed (e.g. saved on the other card): refresh this card's form values too.
  const lastDefaults = useRef({ defaultMobile, defaultEmail });
  useEffect(() => {
    if (lastDefaults.current.defaultMobile !== defaultMobile) setMobile(defaultMobile);
    lastDefaults.current = { defaultMobile, defaultEmail };
  }, [defaultMobile, defaultEmail]);

  // A saved slot (Morning...) that arrives later, e.g. loaded from the backend, opens the slot list.
  const savedCallTime = saved?.callTime;
  useEffect(() => {
    if (savedCallTime && savedCallTime !== "Anytime") setShowSlots(true);
  }, [savedCallTime]);

  // Pencil in the saved view: reload the saved values into the form and open it.
  const openEditor = (): void => {
    if (saved) {
      setMethod(saved.method);
      setMobile(saved.mobile);
      setWhatsApp(whatsAppFor(saved));
    }
    setEditing(true);
  };

  // Check the entry, then wait for the parent to save it; only switch to the saved view on success.
  const handleSave = async (): Promise<void> => {
    const message =
      method === "phone"
        ? // A phone call needs a number with at least one digit (design text); otherwise the shared 10-12 digit rule decides
          !/\d/.test(mobile)
          ? `Enter a valid UK mobile number so ${companyName} can call you.`
          : validatePhoneNumber(mobile)
        : EMAIL_PATTERN.test(defaultEmail.trim())
          ? ""
          : "Please enter a valid email address";
    setError(message);
    onErrorChange?.(message !== "");
    if (message) return;
    const next: ContactDetails = {
      method,
      mobile: mobile.trim(),
      email: defaultEmail.trim(),
      whatsApp: method === "phone" && showWhatsApp ? whatsApp : false,
      callTime: saved ? saved.callTime : "Anytime",
    };
    // Wait for the save: only show the saved view on success, so the old number never flashes.
    setSaving(true);
    const ok = await onSave(next);
    setSaving(false);
    if (ok === false) {
      setError("We couldn't save your details. Please try again.");
      return;
    }
    setLastSaved(next);
    setEditing(false);
  };

  const labelColour = error ? RED : DARK_GREEN;

  if (editing || !saved) {
    return (
      <Box mt={1}>
        <Typography sx={{ fontWeight: 600, color: DARK_GREEN, mb: 1.5 }}>
          How should {companyName} contact you?
        </Typography>

        {/* Phone call / Email chooser */}
        {(["phone", "email"] as const).map((m) => {
          const selected = method === m;
          const chooseMethod = (): void => {
            setMethod(m);
            setError("");
            onErrorChange?.(false);
          };
          return (
            <Box
              key={m}
              role="radio"
              aria-checked={selected}
              tabIndex={0}
              onClick={chooseMethod}
              onKeyDown={onKeyActivate(chooseMethod)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.75,
                mb: 1.5,
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: 16,
                fontWeight: 500,
                color: selected ? "#fff" : ThemeColours.textDark,
                bgcolor: selected ? DARK_GREEN : "#fff",
                border: `1px solid ${selected ? DARK_GREEN : "#d5d5d5"}`,
              }}
            >
              <Box>
                {m === "phone" ? "Phone call" : "Email"}
                {/* Saved number / email shown on its own line under the label */}
                {(m === "phone" ? defaultMobile.trim() : defaultEmail.trim()) && (
                  <Box sx={{ fontWeight: 400, fontSize: 14 }}>
                    {m === "phone" ? formatPhoneNumber(defaultMobile) : defaultEmail.trim()}
                  </Box>
                )}
              </Box>
              <RadioDot selected={selected} light={selected} />
            </Box>
          );
        })}

        {/* Phone: number box plus the WhatsApp tick; Email: email box */}
        {method === "phone" ? (
          <>
            <Typography sx={{ fontWeight: 600, color: labelColour, mt: 2, mb: 1 }}>
              Mobile number
            </Typography>
            {error && (
              <Box sx={{ borderLeft: `3px solid ${RED}`, pl: 1.5, mb: 1 }}>
                <Typography sx={{ color: RED, fontSize: 14 }}>{error}</Typography>
              </Box>
            )}
            <Box
              component="input"
              type="tel"
              value={mobile}
              aria-label="Mobile number"
              // Only digits and "+" can be typed; anything else is dropped as the customer types
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMobile(cleanPhoneNumber(e.target.value))}
              sx={{
                width: "100%",
                boxSizing: "border-box",
                p: 1.75,
                fontSize: 16,
                borderRadius: 2,
                border: `${error ? 2 : 1}px solid ${error ? RED : "#d5d5d5"}`,
                outline: "none",
              }}
            />
            {showWhatsApp && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.75,
                  borderRadius: 2,
                  bgcolor: PALE_GREEN,
                  border: `1px solid ${GREEN}`,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <HiOutlineSpeakerphone size={20} />
                    <Typography sx={{ fontWeight: 600 }}>Also get updates on WhatsApp</Typography>
                  </Box>
                  <Checkbox
                    checked={whatsApp}
                    // The shared Checkbox passes the new true/false value, not the event
                    onChange={(checked: boolean) => setWhatsApp(checked)}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mt: 0.5 }}>
                  <InfoOutlinedIcon sx={{ color: GREEN, fontSize: 16, mt: 0.4 }} />
                  <Typography sx={{ fontSize: 14 }}>
                    {companyName} can also message you on WhatsApp about your free advice. You can turn
                    this off at any time.
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        ) : (
          error && (
            <Box sx={{ borderLeft: `3px solid ${RED}`, pl: 1.5, mt: 2 }}>
              <Typography sx={{ color: RED, fontSize: 14 }}>{error}</Typography>
            </Box>
          )
        )}

        <Button
          variant="contained"
          fullWidth
          disabled={saving}
          onClick={handleSave}
          sx={{ mt: 2, px: "18px", py: { xs: "12px", md: "16px" }, borderRadius: "12px", textTransform: "none", bgcolor: DARK_GREEN }}
        >
          {saving ? "Saving…" : "Save contact details"}
        </Button>
      </Box>
    );
  }

  // Saved view
  return (
    <Box mt={1}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.5,
          borderRadius: 2,
          bgcolor: "#F2F4F5",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {saved.method === "phone" ? <PhoneIcon /> : <EmailIcon sx={{ color: GREEN }} />}
          <Box>
            <Typography sx={{ fontSize: 13, color: "#6C757D" }}>
              {companyName} will connect with you {saved.method === "phone" ? "on" : "at"}
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {saved.method === "phone" ? formatPhoneNumber(saved.mobile) : saved.email}
            </Typography>
          </Box>
        </Box>
        <IconButton aria-label="Edit contact details" onClick={openEditor}>
          <EditIcon />
        </IconButton>
      </Box>

      {/* WhatsApp line: only when the customer ticked WhatsApp */}
      {saved.method === "phone" && saved.whatsApp && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            p: 1.5,
            mt: 1.5,
            borderRadius: 2,
            bgcolor: "#F2F4F5",
          }}
        >
          <CheckCircleIcon sx={{ color: GREEN }} />
          <Typography sx={{ fontSize: 13, color: "#6C757D" }}>
            {companyName} will also message you on WhatsApp about your free debt advice.
          </Typography>
        </Box>
      )}

      {/* Call time (phone only): PayPlan can pick Anytime or one slot; National Debtline always shows the three slots */}
      {saved.method === "phone" && (
        <>
          <Typography sx={{ fontWeight: 700, color: DARK_GREEN, mt: 2, mb: 1 }}>
            When’s best for {companyName} to call?
            {onInfoClick && (
              <IconButton
                aria-label="More information about call times"
                onClick={onInfoClick}
                sx={{ p: 0, ml: 0.7, position: "relative", bottom: 1 }}
              >
                <InfoOutlinedIcon sx={{ color: GREEN, fontSize: 14 }} />
              </IconButton>
            )}
          </Typography>
          {allowAnytime && (
          <Box sx={{ display: "flex", width: "fit-content", borderRadius: 2, overflow: "hidden", border: "1px solid #d5d5d5" }}>
            {(["Anytime", "Choose a time"] as const).map((label) => {
              const selected = label === "Anytime" ? !showSlots : showSlots;
              const chooseMode = (): void => {
                setShowSlots(label !== "Anytime");
                if (label === "Anytime") onCallTimeChange("Anytime");
                else if (saved.callTime === "Anytime") onCallTimeChange(null); // a slot must now be picked
              };
              return (
                <Box
                  key={label}
                  role="radio"
                  aria-checked={selected}
                  tabIndex={0}
                  onClick={chooseMode}
                  onKeyDown={onKeyActivate(chooseMode)}
                  sx={{
                    textAlign: "center",
                    px: 3,
                    py: 1.25,
                    cursor: "pointer",
                    color: selected ? "#fff" : DARK_GREEN,
                    bgcolor: selected ? GREEN : "#fff",
                  }}
                >
                  {label}
                </Box>
              );
            })}
          </Box>
          )}
          {(!allowAnytime || showSlots) && (
            <SlotRows companyName={companyName} selected={saved.callTime ?? undefined} onSelect={onCallTimeChange} />
          )}
        </>
      )}
    </Box>
  );
}

export default ContactPreferenceCard;
